/* ---------------------------------------------------------------------------
   Does THIS browser hold the triangles for an imported model?

   The one question the model index cannot answer. An imported model's geometry is
   not in its state: the vendored io/MeshStore.ts keeps it in IndexedDB, because a
   span is 236 KB and an 18-span flyover 6.5 MB against roughly 5 MB of
   localStorage for an entire origin. The storage shim syncs localStorage, so the
   mesh never left the browser that imported it — while the state it belongs to
   says `hasImportedMesh: true` to every browser that reads it.

   So `imported === true` on the index means "this model displays an imported
   mesh", NOT "there is a mesh here to display". The difference is a bridge drawn
   with no abutments, no columns and nothing saying why, and this module is what
   tells the two apart.

   AND IT IS NOT ONLY ABOUT OTHER PEOPLE'S MACHINES. IndexedDB is keyed by origin —
   scheme, host AND port — so the dev server moving from :8788 to :8790 is a new
   and empty store. That is how the hole was found: change the port, and the model
   is gone.

   READ-ONLY, AND IT REALLY MUST NOT CREATE ANYTHING — this is the sharp edge in
   the file, and an earlier version of it cut. `indexedDB.open(name, 1)` creates
   the database if it is not there, at version 1 with no object stores, after which
   the vendored MeshStore's own `open()` — which asks for version 1 and creates the
   store in `onupgradeneeded` — finds its version already satisfied, skips the
   upgrade, and every save and every load fails against a database with no store in
   it. That was understood. What was missed is that `open(name)` WITH NO VERSION
   creates it just the same, at version 1, because that is what the spec says a
   versionless open of a database that does not exist does. So a check that was
   written to be harmless was in fact the poison: one look at a record whose model
   was imported somewhere else, on a browser that had never imported anything, and
   no mesh could ever be stored on that origin again. Every import after it applied
   correctly on screen, saved no triangles, and came back on the next visit as the
   PARAMETRIC bridge its parameters describe — with the modeller then autosaving
   `hasImportedMesh: false` over the model, which took the register's own
   `imported` flag, its guards and its restore path down with it.

   So the versionless open stays — it must never be the call that decides the
   schema — and the upgrade it can trigger is ABORTED, which rolls the creation
   back and leaves the origin as it was found. A database that is already there
   with no stores at all is the artefact the old code left behind: it can hold
   nothing, so it is DELETED rather than worked around, and the modeller's next
   open creates it properly.

   Every path RESOLVES rather than rejects, following MeshStore's own rule. A
   browser that will not answer — private mode, a locked-down profile, a blocked
   upgrade — is one we cannot make claims about, and the caller is told "no" so it
   offers to restore. Offering a restore nobody needed costs seconds; skipping one
   that was needed costs a bridge.
   --------------------------------------------------------------------------- */

/* Both from the vendored io/MeshStore.ts. Duplicated rather than imported because
   that module is `vendor/`, which this project never edits and never bundles into
   the register's own graph — importing it here would pull the modeller's import
   types into the register's build for two string constants. `npm test` pins these
   against the vendored source, so a drop that renames either fails loudly instead
   of quietly reporting that no device has any mesh. */
const DB_NAME = 'bridge-bim-modeler';
const STORE = 'imported-meshes';

/**
 * Open the mesh store for reading, or resolve null when there is nothing to read.
 *
 * Null covers every case the caller cannot act on and must not guess at: no
 * database, no store in it, a browser that will not answer. The one thing it also
 * does on the way past is REPAIR the store-less database described in the header,
 * because nothing else can — see the note at the missing-store branch.
 */
function openMeshStore(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === 'undefined' || indexedDB === null) return Promise.resolve(null);

  return new Promise((resolve) => {
    let req: IDBOpenDBRequest;
    try {
      // No version: this must never be the call that decides the schema.
      req = indexedDB.open(DB_NAME);
    } catch {
      resolve(null);
      return;
    }

    /* An upgrade here means the database did not exist, so neither does the mesh —
       and this call is not allowed to be the one that brings it into existence. The
       upgrade transaction is ABORTED, which rolls the creation back: the origin is
       left with no database at all, exactly as it was found, and the modeller's own
       open() creates it with its store when it next saves. Without the abort this
       returns "no mesh" and leaves behind a version-1 database with nothing in it,
       which the vendored MeshStore then cannot upgrade and cannot write to — see
       the header. The abort surfaces as an error on the request, which is where
       `false` is answered from. */
    req.onupgradeneeded = () => {
      try {
        req.transaction?.abort();
      } catch {
        /* Nothing to abort, or an engine that will not let us. `onsuccess` below
           deletes the store-less database it leaves, which is the same repair. */
      }
    };
    req.onerror = () => resolve(null);
    req.onblocked = () => resolve(null);

    req.onsuccess = () => {
      const db = req.result;
      try {
        if (!db.objectStoreNames.contains(STORE)) {
          db.close();
          /* A DATABASE WITH NO STORES AT ALL IS AN ARTEFACT, not data. Only an
             aborted or empty creation produces one — the modeller never leaves the
             database without its store — and while it sits there at version 1 the
             modeller's `open(name, 1)` finds its version satisfied, skips the
             upgrade that would add the store, and every mesh save fails. Nothing
             can be lost by removing it and nothing else can repair it, so it goes.
             A database that has OTHER stores is not ours to judge: some later drop
             owns them, and "no mesh here" is the honest answer. */
          if (db.objectStoreNames.length === 0) {
            console.warn(
              `[mesh] "${DB_NAME}" existed with no object stores — deleting it so the ` +
                'modeller can create it properly. Imported models on this device have ' +
                'to be imported again.',
            );
            try {
              indexedDB.deleteDatabase(DB_NAME);
            } catch {
              /* Another tab has it open, or the profile forbids it. The next call
                 tries again; either way this device reports no mesh. */
            }
          }
          resolve(null);
          return;
        }
        resolve(db);
      } catch {
        try {
          db.close();
        } catch {
          /* already closing — nothing to do */
        }
        resolve(null);
      }
    };
  });
}

/**
 * Whether this origin's IndexedDB holds a mesh for `modelId`.
 *
 * False on anything unclear, for the reason in the header: the cost of a wrong
 * "no" is a restore that was not needed, and the cost of a wrong "yes" is a bridge
 * with no substructure.
 */
export async function hasLocalMesh(modelId: string): Promise<boolean> {
  const db = await openMeshStore();
  if (!db) return false;

  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, 'readonly');
      /* `count` rather than `get`: the answer is one integer either way, and a
         `get` would pull a multi-megabyte record across the structured-clone
         boundary only to check that it is not undefined. */
      const q = tx.objectStore(STORE).count(modelId);
      q.onsuccess = () => {
        db.close();
        resolve(typeof q.result === 'number' && q.result > 0);
      };
      q.onerror = () => {
        db.close();
        resolve(false);
      };
    } catch {
      try {
        db.close();
      } catch {
        /* already closing — nothing to do */
      }
      resolve(false);
    }
  });
}

/**
 * Make sure this browser can store a mesh at all, before one is imported into it.
 *
 * ALL IT DOES IS LOOK, and the looking is the repair: `openMeshStore` deletes a
 * store-less database on its way past, which is the one state a browser can be left
 * in where every mesh save fails silently for ever (the header has the whole
 * account). Called before an import rather than only from `hasLocalMesh`, because
 * that check runs only for a model the index already calls imported — and a browser
 * poisoned by the old check has no such model to trigger it. The damage it caused
 * was to models it then reported as parametric, so the repair cannot be reached
 * through them.
 *
 * Never throws and answers nothing. An import that cannot write its triangles is
 * already reported by the record — the point here is to have removed the reason.
 */
export async function repairMeshStore(): Promise<void> {
  const db = await openMeshStore();
  db?.close();
}
